(() => {
  const wishlistKeyBase = 'fortunato:wishlist';
  const wishlistCustomerId = document.querySelector('[data-sl-wishlist-customer-id]')?.dataset.slWishlistCustomerId;
  const wishlistKey = wishlistCustomerId ? `${wishlistKeyBase}:${wishlistCustomerId}` : null;
  const quickViewTriggers = new WeakMap();
  const announcementCleanups = new WeakMap();
  const heroCleanups = new WeakMap();
  const needCarouselCleanups = new WeakMap();
  const qualityBannerCleanups = new WeakMap();
  const packInfoFallbacks = new Map();
  let cartReturnFocus = null;
  let wishlistRenderVersion = 0;
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const closeDetails = (details, restoreFocus = false) => {
    if (!details || !details.open) return;
    const summary = restoreFocus ? details.querySelector(':scope > summary') : null;
    details.open = false;
    if (summary) window.requestAnimationFrame(() => summary.focus());
  };

  const initAnnouncementRotators = (root = document) => {
    root.querySelectorAll('[data-sl-announcement]').forEach((announcement) => {
      if (announcement.dataset.slAnnouncementInitialized === 'true') return;

      const messages = Array.from(announcement.querySelectorAll('[data-sl-announcement-message]'));
      const toggle = announcement.querySelector('[data-sl-announcement-toggle]');
      const rotationEnabled = announcement.dataset.rotationEnabled !== 'false';
      if (messages.length <= 1 || !rotationEnabled) {
        announcement.dataset.slAnnouncementInitialized = 'true';
        return;
      }

      const requestedInterval = Number.parseInt(announcement.dataset.intervalMs || '', 10);
      const interval = Number.isFinite(requestedInterval)
        ? Math.min(12000, Math.max(6000, requestedInterval))
        : 8000;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let activeIndex = Math.max(0, messages.findIndex((message) => message.classList.contains('is-active')));
      let timer = 0;
      let transitionTimer = 0;
      let userPaused = reducedMotion.matches;
      let pointerPaused = false;
      let focusPaused = false;

      const clearTimer = () => {
        window.clearTimeout(timer);
        timer = 0;
      };

      const updateControl = () => {
        if (!toggle) return;
        const label = userPaused ? toggle.dataset.resumeLabel : toggle.dataset.pauseLabel;
        toggle.dataset.state = userPaused ? 'paused' : 'playing';
        toggle.setAttribute('aria-label', label || (userPaused ? 'Resume announcements' : 'Pause announcements'));
      };

      const isTemporarilyPaused = () => pointerPaused || focusPaused || document.hidden;

      const schedule = () => {
        clearTimer();
        if (!announcement.isConnected || userPaused || isTemporarilyPaused()) return;
        timer = window.setTimeout(() => {
          const current = messages[activeIndex];
          const nextIndex = (activeIndex + 1) % messages.length;
          const next = messages[nextIndex];

          next.classList.remove('is-leaving');
          next.setAttribute('aria-hidden', 'false');
          void next.offsetHeight;
          current.classList.remove('is-active');
          current.classList.add('is-leaving');
          current.setAttribute('aria-hidden', 'true');
          next.classList.add('is-active');
          activeIndex = nextIndex;

          window.clearTimeout(transitionTimer);
          transitionTimer = window.setTimeout(() => {
            current.classList.remove('is-leaving');
            transitionTimer = 0;
          }, 340);
          schedule();
        }, interval);
      };

      const sync = () => {
        updateControl();
        schedule();
      };
      const onPointerEnter = () => {
        pointerPaused = true;
        schedule();
      };
      const onPointerLeave = () => {
        pointerPaused = false;
        schedule();
      };
      const onFocusIn = () => {
        focusPaused = true;
        schedule();
      };
      const onFocusOut = () => {
        window.requestAnimationFrame(() => {
          focusPaused = announcement.contains(document.activeElement);
          schedule();
        });
      };
      const onToggle = () => {
        userPaused = !userPaused;
        sync();
      };
      const onVisibilityChange = () => schedule();
      const onReducedMotionChange = (event) => {
        if (event.matches) userPaused = true;
        sync();
      };

      announcement.addEventListener('mouseenter', onPointerEnter);
      announcement.addEventListener('mouseleave', onPointerLeave);
      announcement.addEventListener('focusin', onFocusIn);
      announcement.addEventListener('focusout', onFocusOut);
      if (toggle) toggle.addEventListener('click', onToggle);
      document.addEventListener('visibilitychange', onVisibilityChange);
      if (typeof reducedMotion.addEventListener === 'function') {
        reducedMotion.addEventListener('change', onReducedMotionChange);
      } else {
        reducedMotion.addListener(onReducedMotionChange);
      }

      announcementCleanups.set(announcement, () => {
        clearTimer();
        window.clearTimeout(transitionTimer);
        transitionTimer = 0;
        announcement.removeEventListener('mouseenter', onPointerEnter);
        announcement.removeEventListener('mouseleave', onPointerLeave);
        announcement.removeEventListener('focusin', onFocusIn);
        announcement.removeEventListener('focusout', onFocusOut);
        if (toggle) toggle.removeEventListener('click', onToggle);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        if (typeof reducedMotion.removeEventListener === 'function') {
          reducedMotion.removeEventListener('change', onReducedMotionChange);
        } else {
          reducedMotion.removeListener(onReducedMotionChange);
        }
        delete announcement.dataset.slAnnouncementInitialized;
        announcementCleanups.delete(announcement);
      });

      announcement.dataset.slAnnouncementInitialized = 'true';
      sync();
    });
  };

  const destroyAnnouncementRotators = (root) => {
    root.querySelectorAll('[data-sl-announcement]').forEach((announcement) => {
      announcementCleanups.get(announcement)?.();
    });
  };

  const initHeroRotators = (root = document) => {
    root.querySelectorAll('[data-sl-hero-rotator]').forEach((hero) => {
      if (hero.dataset.slHeroInitialized === 'true') return;

      const slides = Array.from(hero.querySelectorAll('[data-sl-hero-slide]'));
      const tabs = Array.from(hero.querySelectorAll('[data-sl-hero-tab]'));
      const previous = hero.querySelector('[data-sl-hero-previous]');
      const next = hero.querySelector('[data-sl-hero-next]');
      const toggle = hero.querySelector('[data-sl-hero-toggle]');
      const status = hero.querySelector('[data-sl-hero-status]');
      const rotationEnabled = hero.dataset.rotationEnabled !== 'false';
      if (slides.length <= 1) {
        hero.dataset.slHeroInitialized = 'true';
        return;
      }

      const requestedInterval = Number.parseInt(hero.dataset.intervalMs || '', 10);
      const interval = Number.isFinite(requestedInterval)
        ? Math.min(9000, Math.max(6000, requestedInterval))
        : 6500;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer = 0;
      let userPaused = reducedMotion.matches || !rotationEnabled;
      let pointerPaused = false;
      let focusPaused = false;

      const clearTimer = () => {
        window.clearTimeout(timer);
        timer = 0;
      };

      const isTemporarilyPaused = () => pointerPaused || focusPaused || document.hidden;

      const updateControl = () => {
        if (!toggle) return;
        const label = userPaused ? toggle.dataset.resumeLabel : toggle.dataset.pauseLabel;
        toggle.dataset.state = userPaused ? 'paused' : 'playing';
        toggle.setAttribute('aria-label', label || (userPaused ? 'Resume slides' : 'Pause slides'));
        toggle.textContent = label || (userPaused ? 'Resume slides' : 'Pause slides');
      };

      const updateStatus = () => {
        if (!status) return;
        const template = hero.dataset.statusTemplate || 'Slide [current] of [total]';
        status.textContent = template
          .replace('[current]', String(activeIndex + 1))
          .replace('[total]', String(slides.length));
      };

      const render = (announce = true) => {
        slides.forEach((slide, index) => {
          const isActive = index === activeIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', String(!isActive));
          if (isActive) slide.removeAttribute('inert');
          else slide.setAttribute('inert', '');
        });
        tabs.forEach((tab, index) => {
          const isActive = index === activeIndex;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', String(isActive));
          tab.tabIndex = isActive ? 0 : -1;
        });
        if (announce) updateStatus();
      };

      const schedule = () => {
        clearTimer();
        if (!hero.isConnected || userPaused || isTemporarilyPaused()) return;
        timer = window.setTimeout(() => {
          activeIndex = (activeIndex + 1) % slides.length;
          render(false);
          schedule();
        }, interval);
      };

      const show = (nextIndex, announce = true) => {
        activeIndex = (nextIndex + slides.length) % slides.length;
        render(announce);
        schedule();
      };

      const onPrevious = () => show(activeIndex - 1);
      const onNext = () => show(activeIndex + 1);
      const onToggle = () => {
        userPaused = !userPaused;
        updateControl();
        schedule();
      };
      const onPointerEnter = () => {
        pointerPaused = true;
        schedule();
      };
      const onPointerLeave = () => {
        pointerPaused = false;
        schedule();
      };
      const onFocusIn = () => {
        focusPaused = true;
        schedule();
      };
      const onFocusOut = () => {
        window.requestAnimationFrame(() => {
          focusPaused = hero.contains(document.activeElement);
          schedule();
        });
      };
      const onVisibilityChange = () => schedule();
      const onReducedMotionChange = (event) => {
        if (event.matches) userPaused = true;
        updateControl();
        schedule();
      };
      const onTabKeydown = (event) => {
        const tabIndex = tabs.indexOf(event.currentTarget);
        if (tabIndex < 0) return;
        let nextIndex = null;
        if (event.key === 'ArrowLeft') nextIndex = tabIndex - 1;
        if (event.key === 'ArrowRight') nextIndex = tabIndex + 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        nextIndex = (nextIndex + tabs.length) % tabs.length;
        show(nextIndex);
        tabs[nextIndex].focus();
      };
      const tabClickHandlers = tabs.map((tab, index) => () => show(index));

      previous?.addEventListener('click', onPrevious);
      next?.addEventListener('click', onNext);
      toggle?.addEventListener('click', onToggle);
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', tabClickHandlers[index]);
        tab.addEventListener('keydown', onTabKeydown);
      });
      hero.addEventListener('mouseenter', onPointerEnter);
      hero.addEventListener('mouseleave', onPointerLeave);
      hero.addEventListener('focusin', onFocusIn);
      hero.addEventListener('focusout', onFocusOut);
      document.addEventListener('visibilitychange', onVisibilityChange);
      if (typeof reducedMotion.addEventListener === 'function') {
        reducedMotion.addEventListener('change', onReducedMotionChange);
      } else {
        reducedMotion.addListener(onReducedMotionChange);
      }

      heroCleanups.set(hero, () => {
        clearTimer();
        previous?.removeEventListener('click', onPrevious);
        next?.removeEventListener('click', onNext);
        toggle?.removeEventListener('click', onToggle);
        tabs.forEach((tab, index) => {
          tab.removeEventListener('click', tabClickHandlers[index]);
          tab.removeEventListener('keydown', onTabKeydown);
        });
        hero.removeEventListener('mouseenter', onPointerEnter);
        hero.removeEventListener('mouseleave', onPointerLeave);
        hero.removeEventListener('focusin', onFocusIn);
        hero.removeEventListener('focusout', onFocusOut);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        if (typeof reducedMotion.removeEventListener === 'function') {
          reducedMotion.removeEventListener('change', onReducedMotionChange);
        } else {
          reducedMotion.removeListener(onReducedMotionChange);
        }
        delete hero.dataset.slHeroInitialized;
        heroCleanups.delete(hero);
      });

      hero.dataset.slHeroInitialized = 'true';
      render(false);
      updateControl();
      schedule();
    });
  };

  const destroyHeroRotators = (root) => {
    root.querySelectorAll('[data-sl-hero-rotator]').forEach((hero) => {
      heroCleanups.get(hero)?.();
    });
  };

  const initNeedsControllers = (root = document) => {
    root.querySelectorAll('[data-sl-needs-controller]').forEach((controller) => {
      if (controller.dataset.slNeedsInitialized === 'true') return;

      const tabs = Array.from(controller.querySelectorAll('[data-sl-needs-tab]'));
      const panels = Array.from(controller.querySelectorAll('[data-sl-needs-panel]'));
      const concepts = Array.from(controller.querySelectorAll('[data-sl-needs-concept]'));
      const tablist = controller.querySelector('[role="toolbar"]');
      const previous = controller.querySelector('[data-sl-needs-previous]');
      const next = controller.querySelector('[data-sl-needs-next]');
      const toggle = controller.querySelector('[data-sl-needs-toggle]');
      const status = controller.querySelector('[data-sl-needs-status]');
      const announcement = controller.querySelector('[data-sl-needs-announcement]');
      const viewport = controller.querySelector('.sl-needs__viewport');
      const controls = controller.querySelector('.sl-needs__controls');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const desktopLayout = window.matchMedia('(min-width: 900px)');
      const requestedInterval = Number.parseInt(controller.dataset.intervalMs || '', 10);
      const interval = Number.isFinite(requestedInterval)
        ? Math.min(12000, Math.max(6000, requestedInterval))
        : 7000;
      const rotationEnabled = controller.dataset.rotationEnabled !== 'false';
      let activeIndex = Math.max(0, tabs.findIndex((tab) => tab.classList.contains('is-active')));
      let userPaused = reducedMotion.matches || !rotationEnabled || panels.length <= 1;
      let pointerPaused = false;
      let focusPaused = false;
      let timer = 0;
      let motionTimer = 0;
      let motionFrame = 0;
      let motionRevision = 0;
      let announcementRevision = 0;
      let destroyed = false;
      let pointerId = null;
      let pointerStartX = null;
      let pointerStartY = null;

      const clearTimer = () => {
        window.clearTimeout(timer);
        timer = 0;
      };

      const clearMotionWork = () => {
        if (motionFrame) window.cancelAnimationFrame(motionFrame);
        window.clearTimeout(motionTimer);
        motionFrame = 0;
        motionTimer = 0;
      };

      const clearMotionStaging = (revision = null) => {
        panels.forEach((panel) => {
          const ownsRevision = revision === null
            || panel.dataset.slNeedsMotionStage === String(revision);
          if (ownsRevision) delete panel.dataset.slNeedsMotionStage;
        });
      };

      const updateControl = () => {
        if (!toggle) return;
        const autoplayUnavailable = reducedMotion.matches || !rotationEnabled || panels.length <= 1;
        const isPaused = userPaused || autoplayUnavailable || isTemporarilyPaused();
        const label = isPaused ? toggle.dataset.resumeLabel : toggle.dataset.pauseLabel;
        toggle.dataset.state = isPaused ? 'paused' : 'playing';
        toggle.disabled = autoplayUnavailable;
        toggle.hidden = autoplayUnavailable;
        toggle.setAttribute('aria-disabled', String(autoplayUnavailable));
        toggle.setAttribute('aria-hidden', String(autoplayUnavailable));
        toggle.setAttribute('aria-label', label || (isPaused ? 'Resume slides' : 'Pause slides'));
      };

      const updateStatus = (announce = false) => {
        const template = controller.dataset.statusTemplate || 'Slide [current] of [total]';
        const message = template
          .replace('[current]', String(activeIndex + 1))
          .replace('[total]', String(panels.length));
        if (status) status.textContent = message;
        if (!announce || !announcement) return;
        const revision = ++announcementRevision;
        announcement.textContent = '';
        window.requestAnimationFrame(() => {
          if (!destroyed && revision === announcementRevision && controller.isConnected) {
            announcement.textContent = message;
          }
        });
      };

      const updateOrientation = () => {
        if (!tablist) return;
        const orientation = controller.dataset.slNeedsVariant === 'dial' && desktopLayout.matches
          ? 'vertical'
          : 'horizontal';
        tablist.setAttribute('aria-orientation', orientation);
      };

      const setPanelPosition = (panel, position) => {
        panel.dataset.slNeedsPosition = position;
        panel.classList.toggle('is-active', position === 'active');
        panel.classList.toggle('is-next', position === 'next');
        panel.classList.toggle('is-far', position === 'far');
      };

      const exposeActivePanel = () => {
        panels.forEach((panel, index) => {
          const isExposed = index === activeIndex;
          panel.setAttribute('aria-hidden', String(!isExposed));
          if (isExposed) {
            panel.removeAttribute('inert');
            return;
          }
          if (document.activeElement && panel.contains(document.activeElement)) tabs[index]?.focus();
          panel.setAttribute('inert', '');
        });
      };

      const updateSelectors = () => {
        controller.dataset.slNeedsSceneIndex = String(activeIndex);

        tabs.forEach((tab, index) => {
          const isActive = index === activeIndex;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-pressed', String(isActive));
          tab.tabIndex = isActive ? 0 : -1;
        });
      };

      const renderPanels = () => {
        panels.forEach((panel, index) => {
          const relativeIndex = (index - activeIndex + panels.length) % panels.length;
          const position = relativeIndex === 0
            ? 'active'
            : relativeIndex === 1
              ? 'next'
              : relativeIndex === 2
                ? 'far'
                : 'hidden';
          setPanelPosition(panel, position);
        });
        exposeActivePanel();
      };

      const updateConcepts = () => {
        concepts.forEach((concept) => {
          const isActive = concept.dataset.slNeedsConcept === controller.dataset.slNeedsVariant;
          concept.classList.toggle('is-active', isActive);
          concept.setAttribute('aria-pressed', String(isActive));
        });
      };

      const render = (announce = true) => {
        updateSelectors();
        renderPanels();
        updateConcepts();

        updateOrientation();
        updateStatus(announce);
      };

      const settleMotion = (revision) => {
        if (destroyed || revision !== motionRevision) return;
        clearMotionWork();
        clearMotionStaging(revision);
        delete controller.dataset.slNeedsMotion;
        delete controller.dataset.slNeedsMotionRevision;
        render(false);
      };

      const cancelMotion = (settle = true) => {
        motionRevision += 1;
        clearMotionWork();
        clearMotionStaging();
        delete controller.dataset.slNeedsMotion;
        delete controller.dataset.slNeedsMotionRevision;
        if (settle && !destroyed) render(false);
      };

      const directionTo = (nextIndex) => {
        const forwardDistance = (nextIndex - activeIndex + panels.length) % panels.length;
        const backwardDistance = (activeIndex - nextIndex + panels.length) % panels.length;
        return forwardDistance <= backwardDistance ? 'forward' : 'backward';
      };

      const beginMotion = (previousIndex, direction, announce) => {
        const revision = ++motionRevision;
        controller.dataset.slNeedsMotion = direction;
        controller.dataset.slNeedsMotionRevision = String(revision);
        updateSelectors();
        updateConcepts();
        updateOrientation();
        updateStatus(announce);
        exposeActivePanel();

        let stagedPanel = null;
        panels.forEach((panel, index) => {
          if (index === previousIndex) {
            setPanelPosition(panel, `leaving-${direction}`);
            return;
          }
          if (index === activeIndex) {
            if (direction === 'backward') {
              panel.dataset.slNeedsMotionStage = String(revision);
              stagedPanel = panel;
            }
            setPanelPosition(panel, `entering-${direction}`);
            return;
          }
          const relativeIndex = (index - activeIndex + panels.length) % panels.length;
          const position = relativeIndex === 1 ? 'next' : relativeIndex === 2 ? 'far' : 'hidden';
          setPanelPosition(panel, position);
        });
        stagedPanel?.getBoundingClientRect?.();

        motionFrame = window.requestAnimationFrame(() => {
          if (destroyed || revision !== motionRevision) return;
          clearMotionStaging(revision);
          motionFrame = window.requestAnimationFrame(() => {
            if (destroyed || revision !== motionRevision) return;
            motionFrame = 0;
            setPanelPosition(panels[activeIndex], 'active');
            motionTimer = window.setTimeout(() => settleMotion(revision), 720);
          });
        });
      };

      const isTemporarilyPaused = () => pointerPaused || focusPaused || document.hidden;
      const schedule = () => {
        clearTimer();
        if (destroyed || !controller.isConnected || userPaused || reducedMotion.matches || isTemporarilyPaused() || panels.length <= 1) return;
        timer = window.setTimeout(() => {
          show(activeIndex + 1, false, 'forward');
        }, interval);
      };

      const show = (nextIndex, announce = true, requestedDirection = null) => {
        if (destroyed || panels.length === 0) return;
        const normalizedIndex = (nextIndex + panels.length) % panels.length;
        const direction = requestedDirection || directionTo(normalizedIndex);
        if (normalizedIndex === activeIndex) {
          cancelMotion(true);
          updateStatus(announce);
          schedule();
          return;
        }
        const previousIndex = activeIndex;
        cancelMotion(true);
        activeIndex = normalizedIndex;
        if (reducedMotion.matches || controller.dataset.slNeedsVariant !== 'orbit') {
          render(announce);
        } else {
          beginMotion(previousIndex, direction, announce);
        }
        schedule();
      };

      const onPrevious = () => show(activeIndex - 1, true, 'backward');
      const onNext = () => show(activeIndex + 1, true, 'forward');
      const onToggle = () => {
        if (reducedMotion.matches || !rotationEnabled || panels.length <= 1) {
          userPaused = true;
          updateControl();
          schedule();
          return;
        }
        if (!userPaused && isTemporarilyPaused()) {
          pointerPaused = false;
          focusPaused = false;
          userPaused = false;
        } else {
          userPaused = !userPaused;
        }
        updateControl();
        schedule();
      };
      const onPointerEnter = () => {
        pointerPaused = true;
        updateControl();
        schedule();
      };
      const onPointerLeave = () => {
        pointerPaused = false;
        updateControl();
        schedule();
      };
      const onFocusIn = (event) => {
        focusPaused = event.target !== toggle;
        updateControl();
        schedule();
      };
      const onFocusOut = () => {
        window.requestAnimationFrame(() => {
          if (destroyed) return;
          focusPaused = controller.contains(document.activeElement) && document.activeElement !== toggle;
          updateControl();
          schedule();
        });
      };
      const onVisibilityChange = () => {
        if (document.hidden) cancelMotion(true);
        updateControl();
        schedule();
      };
      const onReducedMotionChange = (event) => {
        if (event.matches) userPaused = true;
        cancelMotion(true);
        updateControl();
        schedule();
      };
      const onLayoutChange = () => cancelMotion(true);
      const onTabKeydown = (event) => {
        const tabIndex = tabs.indexOf(event.currentTarget);
        if (tabIndex < 0) return;
        const orientation = tablist?.getAttribute('aria-orientation') || 'horizontal';
        let nextIndex = null;
        if (orientation === 'horizontal' && event.key === 'ArrowLeft') nextIndex = tabIndex - 1;
        if (orientation === 'vertical' && event.key === 'ArrowUp') nextIndex = tabIndex - 1;
        if (orientation === 'horizontal' && event.key === 'ArrowRight') nextIndex = tabIndex + 1;
        if (orientation === 'vertical' && event.key === 'ArrowDown') nextIndex = tabIndex + 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        nextIndex = (nextIndex + tabs.length) % tabs.length;
        show(nextIndex);
        tabs[nextIndex].focus();
      };
      const onPointerDown = (event) => {
        if (!event.isPrimary || event.button !== 0) return;
        if (event.target.closest?.('a[href], button, input, select, textarea, summary, [tabindex], [contenteditable]:not([contenteditable="false"])')) return;
        pointerId = event.pointerId;
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
        try {
          viewport?.setPointerCapture?.(event.pointerId);
        } catch (error) {
          pointerId = null;
          pointerStartX = null;
          pointerStartY = null;
        }
      };
      const resetSwipe = () => {
        const capturedPointerId = pointerId;
        pointerId = null;
        pointerStartX = null;
        pointerStartY = null;
        if (capturedPointerId === null) return;
        try {
          if (viewport?.hasPointerCapture?.(capturedPointerId)) {
            viewport.releasePointerCapture(capturedPointerId);
          }
        } catch (error) {
          // Pointer capture can already be released by the browser.
        }
      };
      const onPointerUp = (event) => {
        if (event.pointerId !== pointerId || pointerStartX === null || pointerStartY === null) return;
        const distanceX = event.clientX - pointerStartX;
        const distanceY = event.clientY - pointerStartY;
        resetSwipe();
        if (Math.abs(distanceX) < 44 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
        if (distanceX > 0) onPrevious();
        else onNext();
      };
      const onBlockSelect = (event) => {
        const selectedBlock = event.target.closest?.('[data-block-id]');
        if (!selectedBlock || !controller.contains(selectedBlock)) return;
        const nextIndex = tabs.findIndex((tab) => tab.dataset.blockId === selectedBlock.dataset.blockId);
        if (nextIndex < 0) return;
        userPaused = true;
        updateControl();
        show(nextIndex);
      };
      const tabClickHandlers = tabs.map((tab, index) => () => show(index));
      const conceptClickHandlers = concepts.map((concept) => () => {
        const variant = concept.dataset.slNeedsConcept;
        if (variant !== 'orbit' && variant !== 'dial') return;
        cancelMotion(true);
        controller.dataset.slNeedsVariant = variant;
        render(false);
      });

      previous?.addEventListener('click', onPrevious);
      next?.addEventListener('click', onNext);
      toggle?.addEventListener('click', onToggle);
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', tabClickHandlers[index]);
        tab.addEventListener('keydown', onTabKeydown);
      });
      concepts.forEach((concept, index) => concept.addEventListener('click', conceptClickHandlers[index]));
      controller.addEventListener('mouseenter', onPointerEnter);
      controller.addEventListener('mouseleave', onPointerLeave);
      controller.addEventListener('focusin', onFocusIn);
      controller.addEventListener('focusout', onFocusOut);
      viewport?.addEventListener('pointerdown', onPointerDown);
      viewport?.addEventListener('pointerup', onPointerUp);
      viewport?.addEventListener('pointercancel', resetSwipe);
      document.addEventListener('visibilitychange', onVisibilityChange);
      document.addEventListener('shopify:block:select', onBlockSelect);
      if (typeof reducedMotion.addEventListener === 'function') {
        reducedMotion.addEventListener('change', onReducedMotionChange);
      } else {
        reducedMotion.addListener(onReducedMotionChange);
      }
      if (typeof desktopLayout.addEventListener === 'function') {
        desktopLayout.addEventListener('change', onLayoutChange);
      } else {
        desktopLayout.addListener(onLayoutChange);
      }

      needCarouselCleanups.set(controller, () => {
        announcementRevision += 1;
        cancelMotion(true);
        destroyed = true;
        clearTimer();
        resetSwipe();
        previous?.removeEventListener('click', onPrevious);
        next?.removeEventListener('click', onNext);
        toggle?.removeEventListener('click', onToggle);
        tabs.forEach((tab, index) => {
          tab.removeEventListener('click', tabClickHandlers[index]);
          tab.removeEventListener('keydown', onTabKeydown);
        });
        concepts.forEach((concept, index) => concept.removeEventListener('click', conceptClickHandlers[index]));
        controller.removeEventListener('mouseenter', onPointerEnter);
        controller.removeEventListener('mouseleave', onPointerLeave);
        controller.removeEventListener('focusin', onFocusIn);
        controller.removeEventListener('focusout', onFocusOut);
        viewport?.removeEventListener('pointerdown', onPointerDown);
        viewport?.removeEventListener('pointerup', onPointerUp);
        viewport?.removeEventListener('pointercancel', resetSwipe);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        document.removeEventListener('shopify:block:select', onBlockSelect);
        if (typeof reducedMotion.removeEventListener === 'function') {
          reducedMotion.removeEventListener('change', onReducedMotionChange);
        } else {
          reducedMotion.removeListener(onReducedMotionChange);
        }
        if (typeof desktopLayout.removeEventListener === 'function') {
          desktopLayout.removeEventListener('change', onLayoutChange);
        } else {
          desktopLayout.removeListener(onLayoutChange);
        }
        delete controller.dataset.slNeedsInitialized;
        needCarouselCleanups.delete(controller);
      });

      controller.dataset.slNeedsInitialized = 'true';
      render(false);
      updateControl();
      schedule();
    });
  };

  const destroyNeedsControllers = (root) => {
    root.querySelectorAll('[data-sl-needs-controller]').forEach((controller) => {
      needCarouselCleanups.get(controller)?.();
    });
  };

  const initQualityBanners = (root = document) => {
    root.querySelectorAll('[data-sl-quality-banner]').forEach((banner) => {
      if (banner.dataset.slQualityInitialized === 'true') return;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reducedMotion.matches || typeof window.IntersectionObserver !== 'function') {
        banner.classList.add('is-visible');
        banner.dataset.slQualityInitialized = 'true';
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        banner.classList.add('is-visible');
        observer.disconnect();
      }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
      observer.observe(banner);
      qualityBannerCleanups.set(banner, () => {
        observer.disconnect();
        delete banner.dataset.slQualityInitialized;
        qualityBannerCleanups.delete(banner);
      });
      banner.dataset.slQualityInitialized = 'true';
    });
  };

  const destroyQualityBanners = (root) => {
    root.querySelectorAll('[data-sl-quality-banner]').forEach((banner) => {
      qualityBannerCleanups.get(banner)?.();
    });
  };

  const initMobileMenus = (root = document) => {
    root.querySelectorAll('[data-sl-mobile-menu]').forEach((details) => {
      if (details.dataset.slMobileInitialized === 'true') return;

      details.addEventListener('toggle', () => {
        document.body.classList.toggle(
          'sl-mobile-menu-open',
          Boolean(document.querySelector('[data-sl-mobile-menu][open]'))
        );

        if (details.open) {
          const closeButton = details.querySelector('[data-sl-mobile-close]');
          window.requestAnimationFrame(() => closeButton?.focus());
        }
      });

      details.dataset.slMobileInitialized = 'true';
    });
  };

  const normalizeWishlist = (value) => {
    if (!Array.isArray(value)) return [];
    const handles = [];
    const seen = new Set();

    value.forEach((handle) => {
      if (typeof handle !== 'string') return;
      const normalizedHandle = handle.trim().toLowerCase();
      if (!/^[a-z0-9][a-z0-9-]*$/.test(normalizedHandle) || seen.has(normalizedHandle)) return;
      seen.add(normalizedHandle);
      handles.push(normalizedHandle);
    });

    return handles.slice(0, 100);
  };

  const readWishlist = () => {
    if (!wishlistKey) return [];
    try {
      const value = JSON.parse(window.localStorage.getItem(wishlistKey) || '[]');
      return normalizeWishlist(value);
    } catch (error) {
      return [];
    }
  };

  const writeWishlist = (handles) => {
    if (!wishlistKey) return false;
    try {
      window.localStorage.setItem(wishlistKey, JSON.stringify(normalizeWishlist(handles)));
    } catch (error) {
      return false;
    }
    return true;
  };

  const updateWishlistButton = (button, selected) => {
    button.setAttribute('aria-pressed', String(selected));
    button.setAttribute('aria-label', selected ? button.dataset.removeLabel : button.dataset.addLabel);
    const visibleLabel = button.querySelector('[data-sl-wishlist-label]');
    if (visibleLabel) visibleLabel.textContent = selected ? button.dataset.removeLabel : button.dataset.addLabel;
  };

  const updateWishlistNavigation = (handles = readWishlist()) => {
    const count = handles.length;
    document.querySelectorAll('[data-sl-wishlist-link]').forEach((link) => {
      const emptyLabel = link.dataset.emptyLabel || 'Wishlist';
      const countLabel = link.dataset.countLabel || 'Wishlist, [count] saved items';
      link.setAttribute('aria-label', count > 0 ? countLabel.replace('[count]', String(count)) : emptyLabel);
      link.classList.toggle('has-items', count > 0);
    });
    document.querySelectorAll('[data-sl-wishlist-count]').forEach((badge) => {
      badge.textContent = String(count);
      if (badge.classList.contains('sl-wishlist-badge')) badge.hidden = count === 0;
    });
  };

  const initWishlist = (root = document) => {
    const handles = readWishlist();
    const selectedHandles = new Set(handles);
    root.querySelectorAll('[data-sl-wishlist]').forEach((button) => {
      updateWishlistButton(button, selectedHandles.has(button.dataset.productHandle));
    });
    updateWishlistNavigation(handles);
  };

  const setWishlistPageState = (page, state) => {
    const loading = page.querySelector('[data-sl-wishlist-loading]');
    const empty = page.querySelector('[data-sl-wishlist-empty]');
    const error = page.querySelector('[data-sl-wishlist-error]');
    const grid = page.querySelector('[data-sl-wishlist-grid]');

    if (loading) loading.hidden = state !== 'loading';
    if (empty) empty.hidden = state !== 'empty';
    if (error) error.hidden = state !== 'error';
    if (grid) grid.hidden = state !== 'ready';
    page.setAttribute('aria-busy', String(state === 'loading'));
  };

  const wishlistStatusText = (page, count) => {
    const template = count === 1 ? page.dataset.countOne : page.dataset.countOther;
    return (template || '[count] saved products').replace('[count]', String(count));
  };

  const fetchWishlistProduct = async (rootUrl, handle) => {
    const normalizedRoot = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    const response = await fetch(`${normalizedRoot}products/${encodeURIComponent(handle)}?view=wishlist-card`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (response.status === 404) return { handle, missing: true };
    if (!response.ok) throw new Error('Wishlist product request failed');

    const markup = await response.text();
    const productDocument = new DOMParser().parseFromString(markup, 'text/html');
    const productNode = productDocument.querySelector('[data-sl-wishlist-product]');
    if (!productNode) return { handle, missing: true };
    return { handle, node: document.importNode(productNode, true) };
  };

  const fetchWishlistProducts = async (rootUrl, handles) => {
    const results = new Array(handles.length);
    let nextIndex = 0;
    const workerCount = Math.min(4, handles.length);
    const workers = Array.from({ length: workerCount }, async () => {
      while (nextIndex < handles.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await fetchWishlistProduct(rootUrl, handles[index]);
      }
    });
    await Promise.all(workers);
    return results;
  };

  const loadWishlistPage = async (page) => {
    const renderVersion = ++wishlistRenderVersion;
    const grid = page.querySelector('[data-sl-wishlist-grid]');
    const notice = page.querySelector('[data-sl-wishlist-notice]');
    const status = page.querySelector('[data-sl-wishlist-status]');
    const handles = readWishlist();

    if (!grid || !status) return;
    grid.replaceChildren();
    if (notice) {
      notice.hidden = true;
      notice.textContent = '';
    }

    if (handles.length === 0) {
      setWishlistPageState(page, 'empty');
      status.textContent = wishlistStatusText(page, 0);
      return;
    }

    setWishlistPageState(page, 'loading');
    status.textContent = page.dataset.loadingLabel || 'Loading saved products';

    try {
      const results = await fetchWishlistProducts(page.dataset.rootUrl || '/', handles);
      if (renderVersion !== wishlistRenderVersion) return;

      const availableResults = results.filter((result) => result?.node);
      const availableHandles = availableResults.map((result) => result.handle);
      const removedCount = handles.length - availableHandles.length;

      if (removedCount > 0) {
        writeWishlist(availableHandles);
        updateWishlistNavigation(availableHandles);
        if (notice) {
          notice.textContent = page.dataset.unavailableLabel || '';
          notice.hidden = notice.textContent === '';
        }
      }

      if (availableResults.length === 0) {
        setWishlistPageState(page, 'empty');
        status.textContent = wishlistStatusText(page, 0);
        return;
      }

      const fragment = document.createDocumentFragment();
      availableResults.forEach((result) => fragment.appendChild(result.node));
      grid.appendChild(fragment);
      setWishlistPageState(page, 'ready');
      status.textContent = wishlistStatusText(page, availableResults.length);
      initWishlist(page);
      initPackInfo(page);
      initQuickViews(page);
      initAjaxProductForms(page);
    } catch (error) {
      if (renderVersion !== wishlistRenderVersion) return;
      setWishlistPageState(page, 'error');
      status.textContent = '';
    }
  };

  const initWishlistPages = (root = document) => {
    root.querySelectorAll('[data-sl-wishlist-page]').forEach((page) => {
      if (page.dataset.slWishlistPageInitialized === 'true') return;
      const retry = page.querySelector('[data-sl-wishlist-retry]');
      retry?.addEventListener('click', () => loadWishlistPage(page));
      page.dataset.slWishlistPageInitialized = 'true';
      loadWishlistPage(page);
    });
  };

  const initPolicyNavigation = (root = document) => {
    root.querySelectorAll('.shopify-policy__body .rte, .fl-policy-page__content').forEach((content) => {
      if (content.querySelector(':scope > .fl-policy-toc')) return;

      const headings = Array.from(content.querySelectorAll('h2, h3'));
      if (headings.length < 2) return;

      const usedIds = new Set(Array.from(document.querySelectorAll('[id]')).map((element) => element.id));
      const slugify = (value) => value
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, '-')
        .replace(/^-+|-+$/g, '') || 'section';

      const nav = document.createElement('nav');
      nav.className = 'fl-policy-toc';
      nav.dataset.flPolicyToc = 'true';

      const language = document.documentElement.lang.toLocaleLowerCase();
      const label = language.startsWith('et')
        ? 'Sellel lehel'
        : language.startsWith('ru')
          ? 'На этой странице'
          : 'On this page';
      nav.setAttribute('aria-label', label);

      const title = document.createElement('p');
      title.className = 'fl-policy-toc__title';
      title.textContent = label;
      nav.append(title);

      const list = document.createElement('ol');
      headings.forEach((heading) => {
        if (!heading.id) {
          const baseId = `policy-${slugify(heading.textContent.trim())}`;
          let candidateId = baseId;
          let suffix = 2;
          while (usedIds.has(candidateId)) {
            candidateId = `${baseId}-${suffix}`;
            suffix += 1;
          }
          heading.id = candidateId;
          usedIds.add(candidateId);
        }

        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        item.append(link);
        list.append(item);
      });
      nav.append(list);
      content.insertBefore(nav, content.firstChild);
    });
  };

  const initPackInfo = (root = document) => {
    const packLabelsFromElement = (element) => ({
      twentyVials: element.dataset.packTwentyVialsLabel || '',
      vegetableCapsules: element.dataset.packVegetableCapsulesLabel || '',
      drinkableSticks: element.dataset.packDrinkableSticksLabel || '',
      drinkableVials: element.dataset.packDrinkableVialsLabel || '',
      vials: element.dataset.packVialsLabel || '',
      bottle: element.dataset.packBottleLabel || '',
      hardCapsules: element.dataset.packHardCapsulesLabel || '',
      softGelCapsules: element.dataset.packSoftGelCapsulesLabel || '',
      netContents: element.dataset.packNetContentsLabel || '',
      capsules: element.dataset.packCapsulesLabel || '',
      capsule: element.dataset.packCapsuleLabel || '',
      servings: element.dataset.packServingsLabel || '',
      serving: element.dataset.packServingLabel || '',
      tablets: element.dataset.packTabletsLabel || '',
      tablet: element.dataset.packTabletLabel || '',
      sachets: element.dataset.packSachetsLabel || '',
      sachet: element.dataset.packSachetLabel || '',
    });

    const replacePackToken = (value, token, localizedToken) => {
      if (!localizedToken) return value;
      const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return value.replace(
        new RegExp(`(^|[^A-Za-z_-])${escapedToken}(?=$|[^A-Za-z_-])`, 'gi'),
        (match, prefix) => `${prefix}${localizedToken}`,
      );
    };

    const normalizePackDisplay = (value, labels) => {
      const display = String(value || '').trim();
      const compact = display.toLowerCase().replace(/\s+/g, '').replace(/×/g, 'x').replace(/–/g, '-');
      const twentyVialFormats = new Set([
        '20x10ml', '20x11ml', '20x25ml', '20vial', '20vials',
        '20single-dosevial', '20single-dosevials', '20singledosevial', '20singledosevials',
        '20ampoule', '20ampoules',
      ]);
      if (twentyVialFormats.has(compact)) return labels.twentyVials || display;

      return [
        ['vegetable capsules', labels.vegetableCapsules],
        ['drinkable sticks', labels.drinkableSticks],
        ['drinkable vials', labels.drinkableVials],
        ['vials', labels.vials],
        ['bottle', labels.bottle],
        ['hard capsules', labels.hardCapsules],
        ['soft gel capsules', labels.softGelCapsules],
        ['net contents', labels.netContents],
        ['capsules', labels.capsules],
        ['capsule', labels.capsule],
        ['servings', labels.servings],
        ['serving', labels.serving],
        ['tablets', labels.tablets],
        ['tablet', labels.tablet],
        ['sachets', labels.sachets],
        ['sachet', labels.sachet],
      ].reduce((result, [token, localizedToken]) => (
        replacePackToken(result, token, localizedToken)
      ), display);
    };

    const loadPackFallback = (productUrl) => {
      const cacheKey = new URL(productUrl, document.baseURI).href;
      const cachedRequest = packInfoFallbacks.get(cacheKey);
      if (cachedRequest) return cachedRequest;

      const request = fetch(productUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Pack fallback request failed with ${response.status}`);
          return response.text();
        })
        .then((markup) => {
          const factBlocks = Array.from(
            markup.matchAll(/<div\b[^>]*class=(["'])[^"']*\bfl-product-fact\b[^"']*\1[^>]*>([\s\S]*?)<\/div>/gi),
          );
          const packFactMarkup = factBlocks[1]?.[2] || '';
          if (!packFactMarkup) return '';
          const packFact = new DOMParser().parseFromString(packFactMarkup, 'text/html');
          return packFact.querySelector('strong')?.textContent.trim() || '';
        })
        .catch(() => {
          packInfoFallbacks.delete(cacheKey);
          return '';
        });

      packInfoFallbacks.set(cacheKey, request);
      return request;
    };

    root.querySelectorAll('[data-sl-pack-info]').forEach(async (element) => {
      const labels = packLabelsFromElement(element);
      if (element.textContent.trim()) {
        element.textContent = normalizePackDisplay(element.textContent, labels);
        return;
      }
      if (element.dataset.packLoading === 'true') return;
      const productUrl = element.dataset.productUrl;
      if (!productUrl) return;
      element.dataset.packLoading = 'true';

      try {
        const packText = await loadPackFallback(productUrl);

        if (packText) element.textContent = normalizePackDisplay(packText, labels);
      } catch (error) {
        return;
      } finally {
        delete element.dataset.packLoading;
      }
    });
  };

  const initProductStickyBar = (root = document) => {
    root.querySelectorAll('[data-fl-mobile-purchase]').forEach((bar) => {
      if (bar.dataset.flInitialized === 'true') return;

      const section = bar.closest('[data-section-type="product"]');
      const sourcePrice = section?.querySelector('[data-product-price]');
      const sourceButton = section?.querySelector('.product-single__form [data-add-to-cart]');
      const stickyPrice = bar.querySelector('[data-fl-mobile-price]');
      const stickyButton = bar.querySelector('[data-fl-mobile-submit]');
      const stickyButtonText = bar.querySelector('[data-fl-mobile-submit-text]');

      if (!section || !sourcePrice || !sourceButton || !stickyPrice || !stickyButton || !stickyButtonText) return;

      const sync = () => {
        stickyPrice.textContent = sourcePrice.textContent.trim();
        stickyButton.disabled = sourceButton.disabled;
        stickyButtonText.textContent = sourceButton.textContent.trim();
      };

      sync();
      new MutationObserver(sync).observe(sourcePrice, { childList: true, characterData: true, subtree: true });
      new MutationObserver(sync).observe(sourceButton, { attributes: true, childList: true, characterData: true, subtree: true });
      bar.dataset.flInitialized = 'true';
    });
  };

  const initQuickViews = (root = document) => {
    root.querySelectorAll('[data-sl-quick-view-dialog]').forEach((dialog) => {
      if (dialog.dataset.slQuickViewInitialized === 'true') return;

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });

      dialog.addEventListener('close', () => {
        const trigger = quickViewTriggers.get(dialog);
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          window.requestAnimationFrame(() => trigger.focus());
        }
        quickViewTriggers.delete(dialog);
        if (!document.querySelector('[data-sl-quick-view-dialog][open]')) {
          document.documentElement.classList.remove('sl-quick-view-open');
        }
      });

      dialog.dataset.slQuickViewInitialized = 'true';
    });
  };

  const openQuickView = (button) => {
    const dialogId = button.getAttribute('aria-controls');
    const dialog = dialogId ? document.getElementById(dialogId) : null;

    if (!dialog || typeof dialog.showModal !== 'function') {
      if (button.dataset.productUrl) window.location.assign(button.dataset.productUrl);
      return;
    }

    if (dialog.open) return;
    quickViewTriggers.set(dialog, button);
    button.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('sl-quick-view-open');
    dialog.showModal();
  };

  const copyShareUrl = async (url) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        return true;
      } catch {
        // Continue with the selection fallback when clipboard access is denied.
      }
    }

    const input = document.createElement('textarea');
    input.value = url;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    try {
      input.select();
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      input.remove();
    }
  };

  const announceShareFeedback = (feedback, message) => {
    if (!feedback) return;
    feedback.textContent = '';
    window.requestAnimationFrame(() => {
      feedback.textContent = message;
    });
  };

  const shareProduct = async (button) => {
    const shareData = {
      title: button.dataset.shareTitle || document.title,
      url: button.dataset.shareUrl || window.location.href
    };
    const feedback = button.dataset.shareFeedbackId
      ? document.getElementById(button.dataset.shareFeedbackId)
      : null;

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    try {
      const copied = await copyShareUrl(shareData.url);
      const message = copied
        ? button.dataset.copiedLabel || 'Product link copied'
        : button.dataset.unavailableLabel || 'Copy unavailable';
      announceShareFeedback(feedback, message);
    } catch {
      announceShareFeedback(feedback, button.dataset.unavailableLabel || 'Copy unavailable');
    }
  };

  const initProductRails = (root = document) => {
    root.querySelectorAll('[data-sl-product-carousel]').forEach((carousel) => {
      if (carousel.dataset.slProductRailInitialized === 'true') return;

      const rail = carousel.querySelector('[data-sl-product-rail]');
      const markers = Array.from(carousel.querySelectorAll('[data-sl-carousel-marker]'));
      const status = carousel.querySelector('[data-sl-carousel-status]');
      const cards = rail ? Array.from(rail.querySelectorAll(':scope > [data-sl-product-card]')) : [];
      if (!rail || cards.length < 2 || markers.length !== cards.length) return;

      let animationFrame = 0;
      let announcementTimer = 0;
      let activeIndex = -1;

      const closestVisibleCard = () => {
        const railRect = rail.getBoundingClientRect();
        let bestIndex = 0;
        let bestVisibleWidth = -1;

        cards.forEach((card, index) => {
          const cardRect = card.getBoundingClientRect();
          const visibleWidth = Math.max(
            0,
            Math.min(cardRect.right, railRect.right) - Math.max(cardRect.left, railRect.left)
          );
          if (visibleWidth > bestVisibleWidth) {
            bestVisibleWidth = visibleWidth;
            bestIndex = index;
          }
        });

        return bestIndex;
      };

      const updateStatus = (index) => {
        if (!status) return;
        const productLabel = status.dataset.productLabel || 'Product';
        const ofLabel = status.dataset.ofLabel || 'of';
        status.textContent = `${productLabel} ${index + 1} ${ofLabel} ${cards.length}`;
      };

      const update = (announce = false) => {
        animationFrame = 0;
        const nextIndex = closestVisibleCard();
        if (nextIndex === activeIndex) return;
        activeIndex = nextIndex;
        markers.forEach((marker, index) => marker.classList.toggle('is-active', index === activeIndex));

        window.clearTimeout(announcementTimer);
        if (announce) announcementTimer = window.setTimeout(() => updateStatus(activeIndex), 140);
        else updateStatus(activeIndex);
      };

      const requestUpdate = (announce = false) => {
        if (animationFrame) return;
        animationFrame = window.requestAnimationFrame(() => update(announce));
      };

      rail.addEventListener('scroll', () => requestUpdate(true), { passive: true });
      if ('onscrollend' in window) rail.addEventListener('scrollend', () => updateStatus(closestVisibleCard()));

      if (window.ResizeObserver) {
        const resizeObserver = new ResizeObserver(() => requestUpdate());
        resizeObserver.observe(rail);
        cards.forEach((card) => resizeObserver.observe(card));
      } else {
        window.addEventListener('resize', () => requestUpdate(), { passive: true });
      }

      carousel.dataset.slProductRailInitialized = 'true';
      requestUpdate();
    });
  };

  const initAjaxProductForms = (root = document) => {
    root.querySelectorAll('.sl-ajax-product-form').forEach((form) => {
      if (form.dataset.slAjaxInitialized === 'true') return;

      form.addEventListener('submit', async (event) => {
        if (!window.fetch) return;
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        const buttonLabel = button?.querySelector('[data-sl-add-label]');
        const defaultIcon = button?.querySelector('[data-sl-add-default-icon]');
        const spinner = button?.querySelector('[data-sl-add-spinner]');
        const addedIcon = button?.querySelector('[data-sl-add-added-icon]');
        if (!button || button.disabled) return;

        const originalText = (buttonLabel?.textContent || button.textContent).trim();
        const loadingText = button.dataset.loadingLabel || originalText;
        const addedText = button.dataset.addedLabel || originalText;
        const setVisualState = (state, label) => {
          button.dataset.slAddState = state;
          if (buttonLabel) buttonLabel.textContent = label;
          if (defaultIcon) defaultIcon.hidden = state === 'loading' || state === 'added';
          if (spinner) spinner.hidden = state !== 'loading';
          if (addedIcon) addedIcon.hidden = state !== 'added';
        };
        const restoreAvailableState = () => {
          setVisualState('available', originalText);
          button.disabled = false;
          button.removeAttribute('aria-busy');
        };
        const showAddedState = () => {
          setVisualState('added', addedText);
          button.disabled = true;
          button.removeAttribute('aria-busy');
          window.setTimeout(restoreAvailableState, 1600);
        };
        let itemAdded = false;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        setVisualState('loading', loadingText);

        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!response.ok) throw new Error('Unable to add item');
          itemAdded = true;

          if (document.querySelector('#CartDrawer')) cartReturnFocus = button;
          form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            bubbles: true,
            detail: { source: 'fortunato-product-card' }
          }));
          showAddedState();
        } catch (error) {
          if (!itemAdded) {
            restoreAvailableState();
            form.submit();
          }
          return;
        } finally {
          if (!itemAdded) restoreAvailableState();
        }
      });

      form.dataset.slAjaxInitialized = 'true';
    });
  };

  const initCartDrawerFocusReturn = () => {
    const drawer = document.querySelector('#CartDrawer');
    if (!drawer || drawer.dataset.slFocusReturnInitialized === 'true') return;

    let pendingFocusObserver = null;
    let pendingFocusTimeout = null;
    const clearPendingFocus = () => {
      pendingFocusObserver?.disconnect();
      pendingFocusObserver = null;
      window.clearTimeout(pendingFocusTimeout);
      pendingFocusTimeout = null;
    };
    const restoreFocus = (target) => {
      clearPendingFocus();
      if (!target?.isConnected) return;

      const focusIfSafe = () => {
        if (!target.isConnected) {
          clearPendingFocus();
          return 'stop';
        }
        const active = document.activeElement;
        const focusIsNeutral = !active
          || active === document.body
          || active === document.documentElement
          || drawer.contains(active);
        if (!focusIsNeutral) {
          clearPendingFocus();
          return 'stop';
        }
        if (target.disabled) return 'wait';
        target.focus({ preventScroll: true });
        clearPendingFocus();
        return 'done';
      };

      if (focusIfSafe() !== 'wait') return;
      pendingFocusObserver = new MutationObserver(() => focusIfSafe());
      pendingFocusObserver.observe(target, {
        attributes: true,
        attributeFilter: ['disabled']
      });
      pendingFocusTimeout = window.setTimeout(clearPendingFocus, 5000);
    };

    const observer = new MutationObserver((records) => {
      const closed = records.some((record) => (
        record.attributeName === 'class'
        && record.oldValue?.split(/\s+/).includes('drawer--is-open')
        && !drawer.classList.contains('drawer--is-open')
      ));
      if (!closed || !cartReturnFocus) return;

      const target = cartReturnFocus;
      cartReturnFocus = null;
      window.requestAnimationFrame(() => restoreFocus(target));
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
    drawer.dataset.slFocusReturnInitialized = 'true';
  };

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-sl-mobile-close]');
    if (closeButton) {
      closeDetails(closeButton.closest('[data-sl-mobile-menu]'), true);
      return;
    }

    const wishlistButton = event.target.closest('[data-sl-wishlist]');
    if (wishlistButton) {
      const handle = wishlistButton.dataset.productHandle;
      if (!handle) return;
      const handles = new Set(readWishlist());
      const selected = !handles.has(handle);
      if (selected) handles.add(handle);
      else handles.delete(handle);
      const nextHandles = [...handles];
      if (!writeWishlist(nextHandles)) return;
      document.querySelectorAll('[data-sl-wishlist]').forEach((button) => {
        if (button.dataset.productHandle === handle) updateWishlistButton(button, selected);
      });
      updateWishlistNavigation(nextHandles);
      document.dispatchEvent(new CustomEvent('fortunato:wishlist-change', {
        detail: { handle, selected, handles: normalizeWishlist(nextHandles) }
      }));
      return;
    }

    const quickViewButton = event.target.closest('[data-sl-quick-view]');
    if (quickViewButton) {
      event.preventDefault();
      openQuickView(quickViewButton);
      return;
    }

    const quickViewClose = event.target.closest('[data-sl-quick-view-close]');
    if (quickViewClose) {
      const dialog = quickViewClose.closest('[data-sl-quick-view-dialog]');
      if (dialog?.open) dialog.close();
      return;
    }

    const shareButton = event.target.closest('[data-share-product]');
    if (shareButton) {
      shareProduct(shareButton);
      return;
    }

    document.querySelectorAll('[data-sl-menu][open]').forEach((details) => {
      if (!details.contains(event.target)) closeDetails(details);
    });
  });

  document.addEventListener('keydown', (event) => {
    const openMobileMenu = document.querySelector('[data-sl-mobile-menu][open]');

    if (event.key === 'Tab' && openMobileMenu) {
      const drawer = openMobileMenu.querySelector('.sl-mobile-drawer');
      const focusableElements = drawer
        ? Array.from(drawer.querySelectorAll(focusableSelector)).filter(
            (element) => element.getClientRects().length > 0
          )
        : [];

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const focusIsOutside = !drawer.contains(document.activeElement);

      if (event.shiftKey && (document.activeElement === firstElement || focusIsOutside)) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && (document.activeElement === lastElement || focusIsOutside)) {
        event.preventDefault();
        firstElement.focus();
      }

      return;
    }

    if (event.key !== 'Escape') return;

    const openLanguage = event.target.closest?.('.sl-language[open]');
    if (openLanguage) {
      event.preventDefault();
      closeDetails(openLanguage, true);
      return;
    }

    document.querySelectorAll('[data-sl-menu][open], [data-sl-mobile-menu][open]').forEach((details) => {
      closeDetails(details, true);
    });
  });

  document.querySelectorAll('[data-sl-localization-select]').forEach((select) => {
    select.addEventListener('change', () => select.form?.submit());
  });

  // Hover photographs only need bandwidth when a mouse or keyboard reaches a card.
  const hoverImageMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
  const loadCardHoverImage = (event) => {
    if (!hoverImageMedia.matches) return;
    const image = event.target.closest?.('[data-sl-product-card]')?.querySelector('[data-sl-hover-image]');
    if (!image || image.dataset.slImageLoading || image.hasAttribute('data-sl-image-ready')) return;
    image.dataset.slImageLoading = 'true';
    const loaded = () => {
      if (image.naturalWidth > 0) image.setAttribute('data-sl-image-ready', '');
      cleanup();
    };
    const failed = () => {
      delete image.dataset.slImageLoading;
      cleanup();
    };
    const cleanup = () => {
      image.removeEventListener('load', loaded);
      image.removeEventListener('error', failed);
    };
    image.addEventListener('load', loaded);
    image.addEventListener('error', failed);
    if (image.dataset.slImageSrcset) image.srcset = image.dataset.slImageSrcset;
    image.src = image.dataset.slImageUrl;
    if (image.complete && image.naturalWidth > 0) loaded();
  };
  document.addEventListener('pointerover', loadCardHoverImage, { passive: true });
  document.addEventListener('focusin', loadCardHoverImage);

  initWishlist();
  initAnnouncementRotators();
  initHeroRotators();
  initNeedsControllers();
  initQualityBanners();
  initWishlistPages();
  initPackInfo();
  initProductStickyBar();
  initMobileMenus();
  initQuickViews();
  initProductRails();
  initAjaxProductForms();
  initCartDrawerFocusReturn();
  initPolicyNavigation();
  document.addEventListener('shopify:section:load', (event) => {
    initWishlist(event.target);
    initAnnouncementRotators(event.target);
    initHeroRotators(event.target);
    initNeedsControllers(event.target);
    initQualityBanners(event.target);
    initWishlistPages(event.target);
    initPackInfo(event.target);
    initProductStickyBar(event.target);
    initMobileMenus(event.target);
    initQuickViews(event.target);
    initProductRails(event.target);
    initAjaxProductForms(event.target);
    initCartDrawerFocusReturn();
    initPolicyNavigation(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyAnnouncementRotators(event.target);
    destroyHeroRotators(event.target);
    destroyNeedsControllers(event.target);
    destroyQualityBanners(event.target);
  });

  document.addEventListener('collection:reloaded', () => {
    initWishlist();
    initPackInfo();
    initQuickViews();
    initProductRails();
    initAjaxProductForms();
  });

  document.addEventListener('cart:updated', () => {
    initPackInfo();
  });

  document.addEventListener('recommendations:loaded', (event) => {
    const recommendationRoot = event.detail?.section || document;
    initWishlist(recommendationRoot);
    initPackInfo(recommendationRoot);
    initQuickViews(recommendationRoot);
    initProductRails(recommendationRoot);
    initAjaxProductForms(recommendationRoot);
  });

  document.addEventListener('fortunato:wishlist-change', () => {
    document.querySelectorAll('[data-sl-wishlist-page]').forEach((page) => loadWishlistPage(page));
  });

  window.addEventListener('storage', (event) => {
    if (!wishlistKey || event.storageArea !== window.localStorage) return;
    if (event.key !== null && event.key !== wishlistKey) return;
    initWishlist();
    document.querySelectorAll('[data-sl-wishlist-page]').forEach((page) => loadWishlistPage(page));
  });
})();
